/**
 * `CertificationAdmin` — pagina admin per la gestione delle Certificazioni.
 *
 * Rotta `/admin/certifications` (admin). Consente di:
 *  - vedere tutte le certificazioni del catalogo generale (con il numero di
 *    assegnatari e le skill collegate);
 *  - crearne di nuove, modificarle ed eliminarle;
 *  - collegare ciascuna certificazione a una o più Skill (relazione N:N);
 *  - assegnare/rimuovere una certificazione a/da un utente;
 *  - importare le certificazioni Credly di un utente (per conto suo).
 *
 * CRUD su `/api/certifications`; assegnazioni su
 * `/api/certifications/:id/assignees`. L'autorizzazione autorevole è dell'API
 * (403 per i non‑admin). Dopo ogni mutazione si invalidano le query pertinenti.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignCertificationRequest,
  CertificationAssigneeDto,
  CertificationDto,
  CreateCertificationRequest,
  ImportCredlyResponse,
  SkillDto,
  UpdateCertificationRequest,
  UserDto,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { ImageUploadField } from '../components/ImageUploadField';
import { CERTIFICATION_PLACEHOLDER_IMAGE } from '../components/CertificationCard';
import {
  Alert,
  Badge,
  Button,
  ChipAutocomplete,
  DataTable,
  Field,
  Icon,
  Loading,
  Modal,
  PageHeader,
  SegmentedControl,
  TextArea,
  TextInput,
  type Column,
  type SortDirection,
} from '../components/ui';

const certificationsKey = ['certifications'] as const;
const skillsKey = ['skills'] as const;
const usersKey = ['users'] as const;

/** Confronto per nome, orientato dalla direzione (per `DataTable`). */
function compareByName(
  a: CertificationDto,
  b: CertificationDto,
  direction: SortDirection,
): number {
  const sign = direction === 'desc' ? -1 : 1;
  const va = a.name.toLowerCase();
  const vb = b.name.toLowerCase();
  if (va < vb) return -sign;
  if (va > vb) return sign;
  return 0;
}

/**
 * Tipo di certificazione a livello di UI. Mappa il flag booleano `isCompany`
 * del dominio (`company` → `true`, `personal` → `false`) sui valori-stringa
 * richiesti dal `SegmentedControl`.
 */
type CertificationKind = 'personal' | 'company';

/** Opzioni del selettore "Tipo di certificazione" (grouped button). */
const CERTIFICATION_KIND_OPTIONS = [
  { value: 'personal', label: 'Personale' },
  { value: 'company', label: 'Aziendale' },
] as const satisfies readonly { value: CertificationKind; label: string }[];

/** Stato di modifica di una certificazione. */
interface EditingState {
  id: string;
  name: string;
  issuer: string;
  description: string;
  url: string;
  imageUrl: string;
  /** `true` se aziendale: niente skill collegate né assegnatari. */
  isCompany: boolean;
  /** Data di conseguimento (solo aziendali), formato `YYYY-MM-DD` o ''. */
  issuedAt: string;
  /** Data di scadenza (solo aziendali), formato `YYYY-MM-DD` o ''. */
  expiresAt: string;
  skillIds: string[];
}

export default function CertificationAdmin() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newIssuer, setNewIssuer] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  /** Tipo della nuova certificazione: aziendale o personale. */
  const [newIsCompany, setNewIsCompany] = useState(false);
  /** Date della nuova certificazione aziendale (formato `YYYY-MM-DD` o ''). */
  const [newIssuedAt, setNewIssuedAt] = useState('');
  const [newExpiresAt, setNewExpiresAt] = useState('');
  const [newSkillIds, setNewSkillIds] = useState<string[]>([]);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [query, setQuery] = useState('');
  /** Filtro per tipo: tutte, solo aziendali o solo personali. */
  const [kindFilter, setKindFilter] = useState<'all' | 'company' | 'personal'>('all');

  /** Filtro per ente: `'all'` oppure il nome dell'issuer. */
  const [issuerFilter, setIssuerFilter] = useState<'all' | string>('all');
  /** Certificazione di cui gestire gli assegnatari (o `null`). */
  const [managing, setManaging] = useState<CertificationDto | null>(null);

  const certificationsQuery = useQuery({
    queryKey: certificationsKey,
    queryFn: () => api.get<CertificationDto[]>('/api/certifications'),
  });
  const skillsQuery = useQuery({
    queryKey: skillsKey,
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: certificationsKey });
  };

  const createCertification = useMutation({
    mutationFn: (body: CreateCertificationRequest) =>
      api.post<CertificationDto>('/api/certifications', body),
    onSuccess: () => {
      setNewName('');
      setNewIssuer('');
      setNewDescription('');
      setNewUrl('');
      setNewImageUrl('');
      setNewIsCompany(false);
      setNewIssuedAt('');
      setNewExpiresAt('');
      setNewSkillIds([]);
      setIsCreateOpen(false);
      invalidate();
    },
  });

  const updateCertification = useMutation({
    mutationFn: (vars: { id: string; body: UpdateCertificationRequest }) =>
      api.put<CertificationDto>(`/api/certifications/${vars.id}`, vars.body),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const deleteCertification = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/certifications/${id}`),
    onSuccess: () => invalidate(),
  });

  const certifications = useMemo(
    () => certificationsQuery.data ?? [],
    [certificationsQuery.data],
  );
  const skills = useMemo(() => skillsQuery.data ?? [], [skillsQuery.data]);
  const skillOptions = useMemo(
    () =>
      [...skills]
        .map((s) => ({ id: s.id, name: s.name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })),
    [skills],
  );
  const skillNameById = useMemo(() => {
    const byId = new Map(skills.map((s) => [s.id, s.name]));
    return (id: string) => byId.get(id) ?? id;
  }, [skills]);

  // Enti presenti nelle certificazioni esistenti (distinti, ordinati), per le
  // opzioni del filtro. Esclusi gli issuer vuoti.
  const issuerOptions = useMemo(() => {
    const issuers = new Set<string>();
    for (const c of certifications) {
      if (c.issuer !== undefined && c.issuer.trim() !== '') issuers.add(c.issuer);
    }
    return [...issuers].sort((a, b) =>
      a.localeCompare(b, 'it', { sensitivity: 'base' }),
    );
  }, [certifications]);

  const normalizedQuery = query.trim().toLowerCase();
  const visible = useMemo(() => {
    let result = certifications;
    if (kindFilter !== 'all') {
      const wantCompany = kindFilter === 'company';
      result = result.filter((c) => (c.isCompany === true) === wantCompany);
    }
    if (issuerFilter !== 'all') {
      result = result.filter((c) => (c.issuer ?? '') === issuerFilter);
    }
    if (normalizedQuery !== '') {
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(normalizedQuery) ||
          (c.issuer ?? '').toLowerCase().includes(normalizedQuery),
      );
    }
    return result;
  }, [certifications, normalizedQuery, issuerFilter, kindFilter]);

  const trimmedNew = newName.trim();
  const trimmedEdit = editing?.name.trim() ?? '';

  const columns = useMemo<Column<CertificationDto>[]>(
    () => [
      {
        key: 'name',
        header: 'Certificazione',
        sortable: true,
        sortComparator: compareByName,
        render: (c) => (
          <div className="coverage-name">
            <img
              src={c.imageUrl ?? CERTIFICATION_PLACEHOLDER_IMAGE}
              alt=""
              aria-hidden="true"
              className="certification-badge__thumb"
              width={28}
              height={28}
            />
            <span className="coverage-name__label">{c.name}</span>
            {c.credlyTemplateId !== undefined && <Badge tone="info">Credly</Badge>}
            {c.linkedinKey !== undefined && <Badge tone="info">LinkedIn</Badge>}
          </div>
        ),
      },
      {
        key: 'type',
        header: 'Tipo',
        sortable: true,
        sortAccessor: (c) => (c.isCompany === true ? 0 : 1),
        render: (c) =>
          c.isCompany === true ? (
            <Badge tone="info">Aziendale</Badge>
          ) : (
            <Badge tone="neutral">Personale</Badge>
          ),
      },
      {
        key: 'skills',
        header: 'Skill collegate',
        render: (c) => (
          <span className="admin-table__assoc">
            {c.isCompany === true || c.skillIds.length === 0
              ? '—'
              : c.skillIds.map(skillNameById).join(', ')}
          </span>
        ),
      },
      {
        key: 'assigned',
        header: 'Assegnatari',
        sortable: true,
        sortAccessor: (c) => c.assignedCount,
        render: (c) =>
          c.isCompany === true || c.assignedCount === 0 ? (
            <span className="admin-table__assoc">—</span>
          ) : (
            <span className="coverage-cell__count">{c.assignedCount}</span>
          ),
      },
      {
        key: 'actions',
        header: 'Azioni',
        className: 'admin-table__actions-col',
        render: (c) => (
          <div className="admin-item__actions">
            {c.isCompany !== true && (
              <Button
                type="button"
                size="sm"
                iconOnly
                aria-label={`Gestisci assegnatari di ${c.name}`}
                title="Assegnatari"
                onClick={() => setManaging(c)}
              >
                <Icon name="user-plus" />
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              iconOnly
              aria-label={`Modifica ${c.name}`}
              title={`Modifica ${c.name}`}
              onClick={() =>
                setEditing({
                  id: c.id,
                  name: c.name,
                  issuer: c.issuer ?? '',
                  description: c.description ?? '',
                  url: c.url ?? '',
                  imageUrl: c.imageUrl ?? '',
                  isCompany: c.isCompany === true,
                  // Le API restituiscono ISO-8601; l'input `date` vuole
                  // `YYYY-MM-DD`. Presenti solo per le aziendali.
                  issuedAt:
                    c.issuedAt != null ? c.issuedAt.slice(0, 10) : '',
                  expiresAt:
                    c.expiresAt != null ? c.expiresAt.slice(0, 10) : '',
                  skillIds: [...c.skillIds],
                })
              }
            >
              <Icon name="edit" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="danger"
              iconOnly
              aria-label={`Elimina ${c.name}`}
              title={`Elimina ${c.name}`}
              onClick={() => deleteCertification.mutate(c.id)}
              loading={deleteCertification.isPending}
            >
              <Icon name="trash" />
            </Button>
          </div>
        ),
      },
    ],
    [skillNameById, deleteCertification],
  );

  return (
    <section className="certification-admin" aria-labelledby="certification-admin-title">
      <PageHeader
        title="Amministrazione — Certificazioni"
        titleId="certification-admin-title"
        subtitle="Gestisci il catalogo delle certificazioni, collegale alle skill e assegnale agli utenti."
        actions={
          <div className="admin-toolbar">
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                setNewName('');
                setNewIssuer('');
                setNewDescription('');
                setNewUrl('');
                setNewImageUrl('');
                setNewIsCompany(false);
                setNewIssuedAt('');
                setNewExpiresAt('');
                setNewSkillIds([]);
                setIsCreateOpen(true);
              }}
            >
              Nuova certificazione
            </Button>
          </div>
        }
      />

      <Field
        id="certification-search"
        label="Cerca certificazione"
        hint="Filtra per tipo ed ente e cerca per nome o ente."
      >
        <div className="competence-search">
          <span className="competence-search__department user-admin__select">
            <select
              id="certification-kind-filter"
              aria-label="Filtra certificazioni per tipo"
              value={kindFilter}
              onChange={(e) =>
                setKindFilter(e.target.value as 'all' | 'company' | 'personal')
              }
            >
              <option value="all">Tutti i tipi</option>
              <option value="company">Aziendale</option>
              <option value="personal">Personale</option>
            </select>
          </span>
          <span className="competence-search__department user-admin__select">
            <select
              id="certification-issuer-filter"
              aria-label="Filtra certificazioni per ente"
              value={issuerFilter}
              disabled={issuerOptions.length === 0}
              onChange={(e) => setIssuerFilter(e.target.value)}
            >
              <option value="all">Tutti gli enti</option>
              {issuerOptions.map((issuer) => (
                <option key={issuer} value={issuer}>
                  {issuer}
                </option>
              ))}
            </select>
          </span>
          <TextInput
            id="certification-search"
            className="competence-search__input"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome o ente…"
            autoComplete="off"
          />
        </div>
      </Field>

      {certificationsQuery.isLoading ? (
        <Loading>Caricamento certificazioni…</Loading>
      ) : certificationsQuery.isError ? (
        <Alert tone="error">Impossibile caricare le certificazioni.</Alert>
      ) : (
        <DataTable
          columns={columns}
          rows={visible}
          rowKey={(c) => c.id}
          defaultSort={{ key: 'name', direction: 'asc' }}
          emptyMessage="Nessuna certificazione. Creane una nuova o importala da Credly (dal profilo di un utente)."
        />
      )}

      {/* Creazione */}
      <Modal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Nuova certificazione"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setIsCreateOpen(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="certification-create-form"
              variant="primary"
              loading={createCertification.isPending}
              disabled={trimmedNew.length === 0}
            >
              Crea
            </Button>
          </>
        }
      >
        <form
          id="certification-create-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmedNew.length === 0) return;
            const body: CreateCertificationRequest = {
              name: trimmedNew,
              ...(newIssuer.trim() !== '' ? { issuer: newIssuer.trim() } : {}),
              ...(newDescription.trim() !== ''
                ? { description: newDescription.trim() }
                : {}),
              ...(newUrl.trim() !== '' ? { url: newUrl.trim() } : {}),
              ...(newImageUrl.trim() !== '' ? { imageUrl: newImageUrl.trim() } : {}),
              isCompany: newIsCompany,
              // Le date valgono solo per le aziendali (input `date` → `YYYY-MM-DD`
              // o `null` se vuoto).
              ...(newIsCompany
                ? {
                    issuedAt: newIssuedAt !== '' ? newIssuedAt : null,
                    expiresAt: newExpiresAt !== '' ? newExpiresAt : null,
                  }
                : {}),
              // Le certificazioni aziendali non hanno skill collegate.
              skillIds: newIsCompany ? [] : newSkillIds,
            };
            createCertification.mutate(body);
          }}
        >
          <Field id="certification-new-name" label="Nome">
            <TextInput
              id="certification-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Es. AWS Certified Solutions Architect"
            />
          </Field>
          <Field
            id="certification-new-company"
            label="Tipo di certificazione"
            hint="Le certificazioni aziendali non si assegnano alle persone e non hanno skill collegate."
          >
            <SegmentedControl<CertificationKind>
              id="certification-new-company"
              label="Tipo di certificazione"
              options={CERTIFICATION_KIND_OPTIONS}
              value={newIsCompany ? 'company' : 'personal'}
              onChange={(kind) => setNewIsCompany(kind === 'company')}
            />
          </Field>
          {newIsCompany && (
            <div className="admin-form__row">
              <Field id="certification-new-issued" label="Conseguita (facoltativa)">
                <TextInput
                  id="certification-new-issued"
                  type="date"
                  value={newIssuedAt}
                  onChange={(e) => setNewIssuedAt(e.target.value)}
                />
              </Field>
              <Field id="certification-new-expires" label="Scadenza (facoltativa)">
                <TextInput
                  id="certification-new-expires"
                  type="date"
                  value={newExpiresAt}
                  onChange={(e) => setNewExpiresAt(e.target.value)}
                />
              </Field>
            </div>
          )}
          <Field id="certification-new-issuer" label="Ente (facoltativo)">
            <TextInput
              id="certification-new-issuer"
              value={newIssuer}
              onChange={(e) => setNewIssuer(e.target.value)}
              placeholder="Es. Amazon Web Services"
            />
          </Field>
          <Field id="certification-new-description" label="Descrizione (facoltativa)">
            <TextArea
              id="certification-new-description"
              rows={3}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
          </Field>
          <Field id="certification-new-url" label="URL (facoltativo)">
            <TextInput
              id="certification-new-url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://…"
            />
          </Field>
          <Field id="certification-new-image" label="Immagine/badge (facoltativa)">
            <ImageUploadField
              api={api}
              value={newImageUrl}
              onChange={setNewImageUrl}
              label="Carica immagine"
            />
          </Field>
          {!newIsCompany && (
            <Field id="certification-new-skills" label="Skill collegate">
              {skillsQuery.isLoading ? (
                <Loading>Caricamento skill…</Loading>
              ) : (
                <ChipAutocomplete
                  id="certification-new-skills"
                  options={skillOptions}
                  selectedIds={newSkillIds}
                  onChange={setNewSkillIds}
                  placeholder="Cerca una skill…"
                  emptyMessage="Nessuna skill corrisponde."
                />
              )}
            </Field>
          )}
        </form>
      </Modal>

      {/* Modifica */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Modifica certificazione"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="certification-edit-form"
              variant="primary"
              loading={updateCertification.isPending}
              disabled={trimmedEdit.length === 0}
            >
              Salva
            </Button>
          </>
        }
      >
        {editing !== null && (
          <form
            id="certification-edit-form"
            className="admin-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (trimmedEdit.length === 0) return;
              const body: UpdateCertificationRequest = {
                name: editing.name.trim(),
                issuer: editing.issuer.trim(),
                description: editing.description.trim(),
                url: editing.url.trim(),
                imageUrl: editing.imageUrl.trim(),
                isCompany: editing.isCompany,
                // Le date valgono solo per le aziendali; se personale il
                // backend le ignora/rimuove.
                ...(editing.isCompany
                  ? {
                      issuedAt: editing.issuedAt !== '' ? editing.issuedAt : null,
                      expiresAt: editing.expiresAt !== '' ? editing.expiresAt : null,
                    }
                  : {}),
                // Le certificazioni aziendali non hanno skill collegate.
                skillIds: editing.isCompany ? [] : editing.skillIds,
              };
              updateCertification.mutate({ id: editing.id, body });
            }}
          >
            <Field id="certification-edit-name" label="Nome">
              <TextInput
                id="certification-edit-name"
                value={editing.name}
                onChange={(e) =>
                  setEditing((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                }
              />
            </Field>
            <Field
              id="certification-edit-company"
              label="Tipo di certificazione"
              hint="Passando ad aziendale verranno rimosse skill collegate e assegnatari."
            >
              <SegmentedControl<CertificationKind>
                id="certification-edit-company"
                label="Tipo di certificazione"
                options={CERTIFICATION_KIND_OPTIONS}
                value={editing.isCompany ? 'company' : 'personal'}
                onChange={(kind) =>
                  setEditing((prev) =>
                    prev ? { ...prev, isCompany: kind === 'company' } : prev,
                  )
                }
              />
            </Field>
            {editing.isCompany && (
              <div className="admin-form__row">
                <Field id="certification-edit-issued" label="Conseguita (facoltativa)">
                  <TextInput
                    id="certification-edit-issued"
                    type="date"
                    value={editing.issuedAt}
                    onChange={(e) =>
                      setEditing((prev) =>
                        prev ? { ...prev, issuedAt: e.target.value } : prev,
                      )
                    }
                  />
                </Field>
                <Field id="certification-edit-expires" label="Scadenza (facoltativa)">
                  <TextInput
                    id="certification-edit-expires"
                    type="date"
                    value={editing.expiresAt}
                    onChange={(e) =>
                      setEditing((prev) =>
                        prev ? { ...prev, expiresAt: e.target.value } : prev,
                      )
                    }
                  />
                </Field>
              </div>
            )}
            <Field id="certification-edit-issuer" label="Ente (facoltativo)">
              <TextInput
                id="certification-edit-issuer"
                value={editing.issuer}
                onChange={(e) =>
                  setEditing((prev) =>
                    prev ? { ...prev, issuer: e.target.value } : prev,
                  )
                }
              />
            </Field>
            <Field id="certification-edit-description" label="Descrizione (facoltativa)">
              <TextArea
                id="certification-edit-description"
                rows={3}
                value={editing.description}
                onChange={(e) =>
                  setEditing((prev) =>
                    prev ? { ...prev, description: e.target.value } : prev,
                  )
                }
              />
            </Field>
            <Field id="certification-edit-url" label="URL (facoltativo)">
              <TextInput
                id="certification-edit-url"
                value={editing.url}
                onChange={(e) =>
                  setEditing((prev) => (prev ? { ...prev, url: e.target.value } : prev))
                }
              />
            </Field>
            <Field id="certification-edit-image" label="Immagine/badge (facoltativa)">
              <ImageUploadField
                api={api}
                value={editing.imageUrl}
                onChange={(next) =>
                  setEditing((prev) => (prev ? { ...prev, imageUrl: next } : prev))
                }
                label="Carica immagine"
              />
            </Field>
            {!editing.isCompany && (
              <Field id="certification-edit-skills" label="Skill collegate">
                <ChipAutocomplete
                  id="certification-edit-skills"
                  options={skillOptions}
                  selectedIds={editing.skillIds}
                  onChange={(next) =>
                    setEditing((prev) => (prev ? { ...prev, skillIds: next } : prev))
                  }
                  placeholder="Cerca una skill…"
                  emptyMessage="Nessuna skill corrisponde."
                />
              </Field>
            )}
          </form>
        )}
      </Modal>

      {/* Gestione assegnatari */}
      {managing !== null && (
        <AssigneesModal
          certification={managing}
          onClose={() => {
            setManaging(null);
            invalidate();
          }}
        />
      )}
    </section>
  );
}

/**
 * `AssigneesModal` — gestione degli utenti a cui è assegnata una certificazione.
 * Mostra gli assegnatari correnti (con possibilità di rimozione) e consente di
 * assegnare un nuovo utente, oltre a lanciare l'import Credly per un utente.
 */
function AssigneesModal({
  certification,
  onClose,
}: {
  certification: CertificationDto;
  onClose: () => void;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [importUserId, setImportUserId] = useState('');
  const [importResult, setImportResult] = useState<ImportCredlyResponse | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: usersKey,
    queryFn: () => api.get<UserDto[]>('/api/users'),
  });
  const assigneesKey = ['certifications', certification.id, 'assignees'] as const;
  const assigneesQuery = useQuery({
    queryKey: assigneesKey,
    queryFn: () =>
      api.get<CertificationAssigneeDto[]>(
        `/api/certifications/${certification.id}/assignees`,
      ),
  });

  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const displayUser = (u: UserDto) => u.displayName || u.email || u.id;

  // Opzioni utente per il ChipAutocomplete (id + etichetta), ordinate per nome.
  const userOptions = useMemo(
    () =>
      [...users]
        .map((u) => ({ id: u.id, name: displayUser(u) }))
        .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })),
    [users],
  );

  // Date dell'assegnazione per userId (input `date`, formato `YYYY-MM-DD` o '').
  interface AssigneeDates {
    issuedAt: string;
    expiresAt: string;
  }

  // Stato locale: id assegnati (ordine) + relative date, inizializzato dagli
  // assegnatari correnti e aggiornato in modo ottimista.
  const [assignedIds, setAssignedIds] = useState<string[]>([]);
  const [dates, setDates] = useState<Record<string, AssigneeDates>>({});
  // URL del badge specifico per userId (modificabile a mano dall'admin).
  const [badgeUrls, setBadgeUrls] = useState<Record<string, string>>({});
  const assigneesData = assigneesQuery.data;
  useEffect(() => {
    if (assigneesData === undefined) return;
    setAssignedIds(assigneesData.map((a) => a.userId));
    const map: Record<string, AssigneeDates> = {};
    const urls: Record<string, string> = {};
    for (const a of assigneesData) {
      map[a.userId] = {
        // Le API restituiscono ISO-8601; l'input `date` vuole `YYYY-MM-DD`.
        issuedAt: a.issuedAt !== null ? a.issuedAt.slice(0, 10) : '',
        expiresAt: a.expiresAt !== null ? a.expiresAt.slice(0, 10) : '',
      };
      urls[a.userId] = a.badgeUrl ?? '';
    }
    setDates(map);
    setBadgeUrls(urls);
  }, [assigneesData]);

  const assign = useMutation({
    mutationFn: (vars: {
      userId: string;
      issuedAt?: string;
      expiresAt?: string;
      // Incluso nel body solo quando valorizzato (anche `null`/'' per
      // rimuoverlo); se `undefined` l'URL salvato non viene toccato.
      badgeUrl?: string | null;
    }) => {
      const body: AssignCertificationRequest = {
        userId: vars.userId,
        issuedAt: vars.issuedAt && vars.issuedAt !== '' ? vars.issuedAt : null,
        expiresAt: vars.expiresAt && vars.expiresAt !== '' ? vars.expiresAt : null,
        ...(vars.badgeUrl !== undefined ? { badgeUrl: vars.badgeUrl } : {}),
      };
      return api.post<void>(
        `/api/certifications/${certification.id}/assignees`,
        body,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: certificationsKey });
    },
  });

  const unassign = useMutation({
    mutationFn: (userId: string) =>
      api.delete<void>(
        `/api/certifications/${certification.id}/assignees/${userId}`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: certificationsKey });
    },
  });

  /**
   * Applica il nuovo insieme di assegnatari scelto nel ChipAutocomplete:
   * calcola gli id aggiunti (→ assegna) e rimossi (→ rimuovi) rispetto allo
   * stato corrente e lancia le mutazioni puntuali. Lo stato locale è aggiornato
   * in modo ottimista; in caso di errore si ripristina e si mostra un avviso.
   */
  const handleAssigneesChange = (next: string[]) => {
    const prev = assignedIds;
    const added = next.filter((id) => !prev.includes(id));
    const removed = prev.filter((id) => !next.includes(id));
    if (added.length === 0 && removed.length === 0) return;

    setAssignError(null);
    setAssignedIds(next);
    setDates((current) => {
      const map = { ...current };
      for (const id of added) map[id] = { issuedAt: '', expiresAt: '' };
      for (const id of removed) delete map[id];
      return map;
    });
    setBadgeUrls((current) => {
      const map = { ...current };
      for (const id of added) map[id] = '';
      for (const id of removed) delete map[id];
      return map;
    });

    Promise.all([
      ...added.map((id) => assign.mutateAsync({ userId: id })),
      ...removed.map((id) => unassign.mutateAsync(id)),
    ])
      .catch(() => {
        // Ripristina dai dati autorevoli e segnala l'errore.
        setAssignedIds(prev);
        setAssignError('Aggiornamento degli assegnatari non riuscito. Riprova.');
      })
      .finally(() => {
        void queryClient.invalidateQueries({ queryKey: assigneesKey });
      });
  };

  /**
   * Aggiorna una data (conseguimento o scadenza) di un assegnatario e persiste
   * la modifica riassegnando la certificazione (upsert idempotente: aggiorna le
   * date senza duplicare). Lo stato locale è aggiornato subito per reattività.
   */
  const handleDateChange = (
    userId: string,
    field: keyof AssigneeDates,
    value: string,
  ) => {
    const nextForUser: AssigneeDates = {
      ...(dates[userId] ?? { issuedAt: '', expiresAt: '' }),
      [field]: value,
    };
    setDates((current) => ({ ...current, [userId]: nextForUser }));
    setAssignError(null);
    assign
      .mutateAsync({
        userId,
        issuedAt: nextForUser.issuedAt,
        expiresAt: nextForUser.expiresAt,
      })
      .catch(() => {
        setAssignError('Salvataggio delle date non riuscito. Riprova.');
      });
  };

  /** Aggiorna lo stato locale dell'URL del badge mentre l'admin digita. */
  const handleBadgeUrlInput = (userId: string, value: string) => {
    setBadgeUrls((current) => ({ ...current, [userId]: value }));
  };

  /**
   * Persiste l'URL del badge dell'assegnatario (su blur). Invia `badgeUrl` nel
   * body: una stringa non vuota lo imposta, '' lo rimuove. Le date correnti
   * sono reinviate così da non azzerarle (l'upsert aggiorna solo i campi dati).
   */
  const handleBadgeUrlCommit = (userId: string) => {
    const d = dates[userId] ?? { issuedAt: '', expiresAt: '' };
    const value = (badgeUrls[userId] ?? '').trim();
    setAssignError(null);
    assign
      .mutateAsync({
        userId,
        issuedAt: d.issuedAt,
        expiresAt: d.expiresAt,
        badgeUrl: value === '' ? null : value,
      })
      .catch(() => {
        setAssignError('Salvataggio del link al badge non riuscito. Riprova.');
      });
  };

  const importCredly = useMutation({
    mutationFn: (userId: string) =>
      api.post<ImportCredlyResponse>(
        `/api/users/${userId}/certifications/import-credly`,
      ),
    onSuccess: (result) => {
      setImportResult(result);
      void queryClient.invalidateQueries({ queryKey: certificationsKey });
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="wide"
      title={`Assegnatari — ${certification.name}`}
      footer={
        <Button type="button" variant="primary" onClick={onClose}>
          Chiudi
        </Button>
      }
    >
      <div className="admin-form">
        <p>
          Questa certificazione è assegnata a{' '}
          <strong>{assignedIds.length}</strong>{' '}
          {assignedIds.length === 1 ? 'persona' : 'persone'}.
        </p>

        <Field
          id="assignees"
          label="Assegnatari"
          hint="Cerca e seleziona gli utenti. Aggiungi un chip per assegnare, rimuovilo per revocare."
        >
          {usersQuery.isLoading || assigneesQuery.isLoading ? (
            <Loading>Caricamento…</Loading>
          ) : (
            <ChipAutocomplete
              id="assignees"
              options={userOptions}
              selectedIds={assignedIds}
              onChange={handleAssigneesChange}
              placeholder="Cerca un utente…"
              emptyMessage="Nessun utente corrisponde."
            />
          )}
        </Field>

        {assignedIds.length > 0 && (
          <table className="skill-weight-editor__table certification-dates-table">
            <caption className="sr-only">
              Date di conseguimento e scadenza per ciascun assegnatario
            </caption>
            <thead>
              <tr>
                <th scope="col">Utente</th>
                <th scope="col">Conseguita</th>
                <th scope="col">Scadenza</th>
                <th scope="col">Link al badge</th>
              </tr>
            </thead>
            <tbody>
              {assignedIds.map((userId) => {
                const user = usersById.get(userId);
                const label = user ? displayUser(user) : userId;
                const d = dates[userId] ?? { issuedAt: '', expiresAt: '' };
                const badgeUrl = badgeUrls[userId] ?? '';
                return (
                  <tr key={userId}>
                    <td>
                      <span className="skill-weight-editor__skill-label">{label}</span>
                    </td>
                    <td>
                      <TextInput
                        type="date"
                        aria-label={`Data di conseguimento di ${label}`}
                        value={d.issuedAt}
                        onChange={(e) =>
                          handleDateChange(userId, 'issuedAt', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <TextInput
                        type="date"
                        aria-label={`Data di scadenza di ${label}`}
                        value={d.expiresAt}
                        onChange={(e) =>
                          handleDateChange(userId, 'expiresAt', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <TextInput
                        type="url"
                        aria-label={`Link al badge di ${label}`}
                        placeholder="https://www.credly.com/badges/…"
                        value={badgeUrl}
                        onChange={(e) => handleBadgeUrlInput(userId, e.target.value)}
                        onBlur={() => handleBadgeUrlCommit(userId)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {assignError !== null && <Alert tone="error">{assignError}</Alert>}

        <div className="user-menu__divider" role="separator" />

        <Field
          id="import-credly-user"
          label="Importa le certificazioni Credly di un utente"
          hint="Importa nel profilo dell'utente le certificazioni dal suo profilo Credly collegato. Se l'utente non ha un profilo collegato, si tenta comunque con lo slug nome-cognome; se il profilo pubblico esiste viene collegato in automatico. Le certificazioni non ancora a catalogo vengono create."
        >
          <div className="competence-search">
            <span className="user-admin__select competence-search__department">
              <select
                id="import-credly-user"
                value={importUserId}
                disabled={usersQuery.isLoading}
                onChange={(e) => setImportUserId(e.target.value)}
              >
                <option value="">Seleziona un utente…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {displayUser(u)}
                    {u.credlyProfileUrl === null ? ' (nessun Credly, tentativo nome-cognome)' : ''}
                  </option>
                ))}
              </select>
            </span>
            <Button
              type="button"
              variant="default"
              disabled={importUserId === '' || importCredly.isPending}
              loading={importCredly.isPending}
              onClick={() => importUserId !== '' && importCredly.mutate(importUserId)}
            >
              Importa da Credly
            </Button>
          </div>
        </Field>

        {importCredly.isError && (
          <Alert tone="error">
            Import non riuscito. Se l'utente non ha un profilo Credly collegato,
            non è stato trovato alcun profilo pubblico con lo slug nome-cognome:
            collega manualmente il profilo Credly e riprova.
          </Alert>
        )}
        {importResult && (
          <Alert tone="success">
            Import completato: {importResult.imported} nuove,{' '}
            {importResult.skipped} già presenti, {importResult.removed} rimosse,{' '}
            {importResult.createdCertifications} certificazioni create a catalogo (su{' '}
            {importResult.fetched} badge letti).
          </Alert>
        )}
      </div>
    </Modal>
  );
}
